import type { ReadBranchType } from '@readme/api/src/mappings/branch/types';
import type { CreateBranchType } from '@readme/iso/src/branches/mock';

/**
 * Represents an "ephemeral" branch that stores state during the create branch
 * flow.
 *
 * ⚠️ Note that this shape is different from the `CreateBranchType` shape from
 * the API. Specifically, the create branch flow needs to track the base version
 * it was launched from and the branch `name` suffix after the "_" delimiter
 * that gets input into the form from the user. Whereas the API type has a
 * single `name` which is the combined full branch name.
 *
 * @example
 * ```js
 * const ephemeralBranch: EphemeralBranch = {
 *   base: '1.0',
 *   name: 'my-branch'
 * }
 *
 * const createBranchPayload: CreateBranchType = {
 *   name: '1.0_my-branch'
 * }
 * ```
 */
export interface EphemeralBranch {
  base: ReadBranchType['data']['name'];
  name: CreateBranchType['name'];
  source: 'recipes-modal' | 'versions-panel';
}

/**
 * Returns an "empty" ephemeral branch object filled with default values used by
 * the create branch form.
 *
 * @see EphemeralBranch
 */
export function createEphemeralBranch<Keys extends EphemeralBranch>(keys: Partial<Keys>) {
  return {
    base: '',
    name: '',
    source: 'versions-panel',
    ...keys,
  } satisfies EphemeralBranch;
}
